
// src/pages/prayers/components/ActionDropdownMenu.tsx
import React from 'react';
import {
  Share,
  Bookmark,
  CheckCircle,
  Edit,
  Flag,
  Trash2,
  MoreVertical,
  UserX,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Prayer } from '@/types/prayer';
import { LAYERS } from '@/utils/layers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { getAuthorId, isOwnedBy } from '@/utils/ownership';

interface ActionDropdownMenuProps {
  prayer: Prayer;
  onShare: (id: string) => void;
  onBookmark: (id: string) => void;
  onPrayerStatus?: (id: string) => void;
  onEdit?: (id: string) => void;
  onReport?: (id: string) => void;
  onDelete?: (id: string) => void;
  onBlockUser?: (userId: string, username?: string) => void;
  isBookmarked?: boolean;
  baseButton: string;
  accentGlow: string;
  positioning?: string;
  currentUserId?: string;
}

export default function ActionDropdownMenu({
  prayer,
  onShare,
  onBookmark,
  onPrayerStatus,
  onEdit,
  onReport,
  onDelete,
  onBlockUser,
  isBookmarked = false,
  baseButton,
  accentGlow,
  currentUserId,
}: ActionDropdownMenuProps) {
  const { id } = prayer;
  const [isBlockConfirmOpen, setIsBlockConfirmOpen] = React.useState(false);
 
   return (<>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`${baseButton} ${accentGlow} transition-all duration-200 text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300 flex items-center justify-center leading-none relative z-[120] pointer-events-auto`}
          tabIndex={0}
          aria-label="More options"
          style={{ touchAction: 'manipulation' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <MoreVertical size={16} className="w-4 h-4 shrink-0" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        side="left"
        className="bg-background border-border text-foreground shadow-md backdrop-blur-sm"
        style={{ zIndex: LAYERS.SECONDARY_DIALOGS }}
      >
        {/* Share */}
        <DropdownMenuItem
          className="hover:bg-accent hover:text-accent-foreground text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onShare(id);
          }}
        >
          <Share size={14} className="mr-2" />
          Share
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-border" />

        {/* Bookmark */}
        <DropdownMenuItem
          className="hover:bg-accent hover:text-accent-foreground text-xs"
          onClick={(e) => {
            e.stopPropagation();
            console.log('🎯 ActionDropdownMenu: Bookmark clicked for prayer:', id);
            onBookmark(id);
          }}
        >
          <Bookmark
            size={14}
            className={`mr-2 ${
              prayer.isBookmarked 
                ? 'text-purple-600 dark:text-purple-400' 
                : 'text-muted-foreground'
            }`}
            fill={prayer.isBookmarked ? 'currentColor' : 'none'}
          />
          {prayer.isBookmarked ? 'Remove Bookmark' : 'Bookmark'}
        </DropdownMenuItem>

        <DropdownMenuSeparator className="bg-border" />

        {/* Prayer Status */}
        {onPrayerStatus && (
          <DropdownMenuItem
            className="hover:bg-accent hover:text-accent-foreground text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onPrayerStatus(id);
            }}
          >
            <CheckCircle size={14} className="mr-2" />
            Prayer Status
          </DropdownMenuItem>
        )}

        {/* Edit */}
        {onEdit && (
          <DropdownMenuItem
            className="hover:bg-accent hover:text-accent-foreground text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(id);
            }}
          >
            <Edit size={14} className="mr-2" />
            Edit Prayer
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator className="bg-border" />

        {/* Block User - Only show for other users' prayers */}
        {onBlockUser && currentUserId && !isOwnedBy(prayer, currentUserId) && (
          <DropdownMenuItem
            className="hover:bg-accent hover:text-accent-foreground text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300"
            onClick={(e) => {
              e.stopPropagation();
              setIsBlockConfirmOpen(true);
            }}
          >
            <UserX size={14} className="mr-2" />
            Block User
          </DropdownMenuItem>
        )}

        {/* Report */}
        {onReport && (
          <DropdownMenuItem
            className="hover:bg-accent hover:text-accent-foreground text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            onClick={(e) => {
              e.stopPropagation();
              onReport(id);
            }}
          >
            <Flag size={14} className="mr-2" />
            Report
          </DropdownMenuItem>
        )}

        {/* Delete */}
        {onDelete && (
          <DropdownMenuItem
            className="hover:bg-accent hover:text-accent-foreground text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(id);
            }}
          >
            <Trash2 size={14} className="mr-2" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    {/* Confirm Block User Dialog */}
    <AlertDialog open={isBlockConfirmOpen} onOpenChange={setIsBlockConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Block this user?</AlertDialogTitle>
          <AlertDialogDescription>
            You will no longer see prayers from {prayer.author}. You can manage blocked users anytime in Settings → Privacy.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const authorId = getAuthorId(prayer);
              if (authorId) {
                onBlockUser?.(authorId, prayer.author);
              }
              setIsBlockConfirmOpen(false);
            }}
          >
            Block User
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </> );
}
